import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Code } from 'lucide-react';

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/blog', label: 'Blog' },
  { path: '/certifications', label: 'Certifications' },
  { path: '/contact', label: 'Contact' },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        padding: scrolled ? '12px 0' : '20px 0',
        background: scrolled
          ? 'rgba(15, 23, 42, 0.92)'
          : 'linear-gradient(to right, #1E3A5F, #0F172A)',
        backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(63, 63, 70, 0.3)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.2)' : 'none',
        transition: 'all 0.4s cubic-bezier(.22,1,.36,1)',
      }}
    >
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Logo */}
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: 'white',
            }}
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  background: 'linear-gradient(135deg, var(--color-primary-light), var(--color-secondary))',
                  borderRadius: '50%',
                  filter: 'blur(6px)',
                  opacity: 0.6,
                }}
              />
              <div
                style={{
                  position: 'relative',
                  background: 'rgba(24,24,27,0.8)',
                  borderRadius: '50%',
                  padding: '6px',
                  display: 'flex',
                }}
              >
                <Code size={22} style={{ color: 'var(--color-secondary)' }} />
              </div>
            </div>
            <span
              style={{
                fontSize: '1.35rem',
                fontWeight: 800,
                fontFamily: 'var(--font-heading)',
                letterSpacing: '-0.02em',
              }}
            >
              Fret
              <span
                style={{
                  background: 'linear-gradient(135deg, var(--color-secondary), var(--color-primary-light))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Dev
              </span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav
            style={{
              alignItems: 'center',
              gap: '4px',
            }}
            className="hidden md:flex"
          >
            {navItems.map((item) => {
              const active = isActive(item.path);
              const hovered = hoveredLink === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onMouseEnter={() => setHoveredLink(item.path)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{
                    position: 'relative',
                    padding: '8px 18px',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: active ? 600 : 500,
                    color: active ? '#fff' : hovered ? '#fff' : 'rgba(255,255,255,0.65)',
                    background: active
                      ? 'rgba(245,158,11,0.15)'
                      : hovered
                      ? 'rgba(255,255,255,0.06)'
                      : 'transparent',
                    textDecoration: 'none',
                    transition: 'all 0.25s cubic-bezier(.22,1,.36,1)',
                    overflow: 'hidden',
                  }}
                >
                  {item.label}
                  <span
                    style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: active ? '18px' : '0px',
                      height: '2.5px',
                      borderRadius: '2px',
                      background: 'linear-gradient(90deg, var(--color-secondary), var(--color-primary-light))',
                      transition: 'width 0.3s cubic-bezier(.22,1,.36,1)',
                      opacity: active ? 1 : 0,
                    }}
                  />
                </Link>
              );
            })}

            {/* Get a Quote CTA */}
            <Link
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginLeft: '12px',
                padding: '9px 20px',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#0F172A',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: 700,
                textDecoration: 'none',
                transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                boxShadow: '0 2px 12px rgba(245,158,11,0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(245,158,11,0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(245,158,11,0.3)';
              }}
            >
              Get a Quote
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="mobile-menu-btn"
            aria-label="Toggle menu"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.25s',
            }}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          style={{
            maxHeight: isMenuOpen ? '500px' : '0px',
            overflow: 'hidden',
            transition: 'max-height 0.4s cubic-bezier(.22,1,.36,1), opacity 0.3s',
            opacity: isMenuOpen ? 1 : 0,
          }}
        >
          <nav
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              paddingTop: '16px',
              marginTop: '16px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: active ? 600 : 500,
                    color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                    background: active ? 'rgba(245,158,11,0.15)' : 'transparent',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    borderLeft: active ? '3px solid var(--color-secondary)' : '3px solid transparent',
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '8px',
                padding: '14px',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#0F172A',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
